import { Menu, PanelRight, Search } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import { PRIMARY_NAV_ITEMS } from './nav-config';

import { NotificationsBell } from '@/features/notifications';
import { GlassNav } from '@/shared/components/ui/glass-nav';
import { cn } from '@/shared/lib/cn';

export interface TopbarProps {
  /** Mostra o botao hamburger (mobile/tablet drawer). */
  onOpenSidebar?: () => void;
  /** Quando definido, mostra o botao para abrir o right-pane drawer (intermediario). */
  onOpenRightPane?: () => void;
  testID?: string;
}

const ROUTE_TITLES: Array<{ prefix: string; title: string }> = [
  { prefix: '/home', title: 'Início' },
  { prefix: '/expenses', title: 'Despesa' },
  { prefix: '/transactions', title: 'Transações' },
  { prefix: '/groups', title: 'Grupos' },
  { prefix: '/reports', title: 'Relatórios' },
  { prefix: '/profile', title: 'Perfil' },
  { prefix: '/notifications', title: 'Notificações' },
];

function pickTitle(pathname: string): string {
  const match = ROUTE_TITLES.find(({ prefix }) => pathname.startsWith(prefix));
  if (match) return match.title;
  const fallback = PRIMARY_NAV_ITEMS.find((item) =>
    item.matchPaths.some((p) => pathname.startsWith(p)),
  );
  return fallback?.label ?? 'Grana';
}

export function Topbar({ onOpenSidebar, onOpenRightPane, testID = 'app-topbar' }: TopbarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const title = pickTitle(location.pathname);

  return (
    <GlassNav
      as="header"
      data-testid={testID}
      className="sticky top-0 z-[var(--z-header)] flex h-14 items-center gap-2 px-3 md:h-[60px] md:px-6"
    >
      {onOpenSidebar ? (
        <IconButton
          label="Abrir menu"
          testID="topbar-hamburger"
          onClick={onOpenSidebar}
          icon={<Menu size={20} aria-hidden />}
        />
      ) : null}

      <h1
        className="truncate text-display-sm text-on-surface md:text-display-md"
        data-testid="topbar-title"
      >
        {title}
      </h1>

      <div className="ml-auto flex items-center gap-1 md:gap-2">
        <IconButton
          label="Buscar"
          testID="topbar-search"
          onClick={() => navigate('/reports/history')}
          icon={<Search size={20} aria-hidden />}
        />
        <NotificationsBell testID="topbar-notifications" />
        {onOpenRightPane ? (
          <IconButton
            label="Abrir painel lateral"
            testID="topbar-rightpane-toggle"
            onClick={onOpenRightPane}
            icon={<PanelRight size={20} aria-hidden />}
          />
        ) : null}
      </div>
    </GlassNav>
  );
}

interface IconButtonProps {
  label: string;
  testID?: string;
  onClick?: () => void;
  icon: React.ReactNode;
}

function IconButton({ label, testID, onClick, icon }: IconButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      data-testid={testID}
      onClick={onClick}
      className={cn(
        'inline-flex h-11 w-11 items-center justify-center rounded-base text-secondary-text transition-colors hover:bg-surface-high hover:text-on-surface',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
      )}
    >
      {icon}
    </button>
  );
}
